import { MouseEvent, useCallback, useEffect, useMemo, useRef } from 'react';
import { Button, ButtonSizeLG, ButtonSizeMD, ButtonSkinPrimary, ButtonSkinSecondary, ButtonVariants } from '@picsart/button';
import { FontWeights, Spacings, Statuses, Texts, Typography } from '@picsart/cascade';
import { Grid } from '@picsart/grid';
import { ImageFrame } from '@picsart/imageframe';
import { TypographyHorizontalAlign, Text } from '@picsart/text';
import { View } from '@picsart/view';
import IconChevronLeftLarge from '@picsart/icons/IconChevronLeftLarge';
import IconWarningCircleOutline from '@picsart/icons/IconWarningCircleOutline';
import IconWarningTriangleOutline from '@picsart/icons/IconWarningTriangleOutline';
import Image from '@picsart/image/Image';
import IconDownload from '@picsart/icons/IconDownload';
import { Loader, LoaderVariants, LoaderSizeLG } from '@picsart/loader';
import { IconCrossLarge } from '@picsart/icons';
import { useTranslator } from '@picsart/rc/services/localization';
import { getContext } from '@picsart/miniapps-sdk';
import { v4 as uuid } from 'uuid';
import { createReplayImageLayer } from '@picsart/web-replay-sdk/context';
import useStyles from './useStyles';
import { ResultImage, PendingImage } from '../../types';
import { TaskItemStatus, defaultConfig } from '../../constants';

import analytics from '../../utils/analytics';

import Layout, { Header, Body, Footer } from '../Layout';

const pendingPrefix = 'pending-';

interface IResult {
  pageConfig: any;
  images: ResultImage[];
  text: string;
  limitReached: boolean;
  onSelect: (images: ResultImage[]) => void;
  loadMore: () => void;
  edit: () => void;
  onBackClick: () => void;
}

// TODO: implement not only free
const {
  usage_limitation_config: { free },
  result_page_config: { mature_info },
} = defaultConfig;

function Results({ text, images, limitReached, onSelect, loadMore, pageConfig, onBackClick }: IResult) {
  const classes = useStyles();
  const translate = useTranslator();
  const bottomRef = useRef<HTMLDivElement>(null);

  const pendingResults = images.filter(i => i.id.startsWith(pendingPrefix)).length > 0;

  const memoizedImages = useMemo<PendingImage[]>(
    () =>
      images.map(({ url, ...restProps }) => ({
        url: restProps.status === TaskItemStatus.DONE ? url : undefined,
        ...restProps,
      })),
    [images],
  );

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [images.length]);

  const isImagePending = useCallback(({ id, status }: ResultImage | PendingImage) => {
    return id.startsWith(pendingPrefix) || status !== TaskItemStatus.DONE;
  }, []);

  const handleImageClick = (image: ResultImage | PendingImage) => {
    if (pendingResults || isImagePending(image)) return;

    onSelect([image as ResultImage]);
    analytics.objectApply({ miniapp_package_id: 'ai_background', item_id: image.id, tool: 'photo', source: 'ai-background' });
  };

  const handleImageDownload = async (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement | HTMLLabelElement>, image: ResultImage | PendingImage) => {
    event?.stopPropagation();
    if (isImagePending(image)) return;
    if (!image?.url) return;

    // creating the resource for the image
    const uniqueId = uuid();
    const layerId = image?.id;
    createReplayImageLayer({
      name: 'Background image',
      uuid: layerId,
      locked: false,
      fallbackLayer: layerId,
      meta: {},
      mediaType: 'BACKGROUND_IMAGE',
      props: {
        blendMode: 'xor',
        hidden: false,
        opacity: 1,
        rotation: 0,
        x: 0,
        y: 0,
        image: {
          width: 1080,
          height: 1080,
          location: image.url,
          type: 'photo',
          id: layerId,
        },
      },
    });

    const fileDate = new Date().toISOString().split('T')[0];
    const fileName = `AI_Background_${fileDate}_${image.id}.png`;

    const res = await getContext().handlers.download([
      {
        url: image?.url,
        name: translate(fileName),
        mimeType: 'image/png',
      },
    ]);

    if (res.success) {
      analytics.objectExport({ id: image.id, destination: 'local', source: 'ai-background' });
      analytics.objectApply({ miniapp_package_id: 'ai_background', item_id: image.id, tool: 'photo', source: 'ai-background' });
    }
  };

  return (
    <View className={classes.mainContent} style={{ height: window.innerHeight }}>
      <Layout>
        <Header>
          <View className={classes.header} marginBlockEnd={Spacings.s12} paddingBlockStart={Spacings.s16} justifyContent="space-between" flexDirection="row">
            <View marginBlock={Spacings.s12} alignItems="center" gap={Spacings.s12} width="100%">
              <IconCrossLarge data-testId="closeIcon" fill={Texts.base} className={classes.closeIcon} onClick={() => getContext().handlers.close()} />
              <Text size={Typography.t6} maxVisibleLinesCount={1} color={Texts.base} weight={FontWeights.Bold}>
                {pageConfig.title}
              </Text>
            </View>
          </View>

          <View alignItems="center" flexDirection="column">
            {text.length > 0 && (
              <Text size={Typography.t5} weight={FontWeights.SemiBold} color={Texts.base}>
                {text.length > 290 ? `"${text.slice(0, 280)}..."` : `"${text}"`}
              </Text>
            )}
          </View>
        </Header>

        <Body>
          <View marginBlockStart={Spacings.s24}>
            <Grid columns="1fr 1fr" gap={Spacings.s8}>
              {memoizedImages.map(image => (
                <View key={image.id}>
                  <ImageFrame
                    {...(image.url && {
                      width: 'auto',
                      height: 'auto',
                      contentClassName: classes.imageFrameContent,
                    })}
                    className={classes.imageLoading}
                    data-testid={image.id}
                    onClick={() => handleImageClick(image)}
                  >
                    {image.url && TaskItemStatus.FAILED_NSFW !== image.status && <Image dataTestId="generatedImage" src={image.url ? `${image.url}?to=fixed&r=160` : image.url} />}

                    {!image.url && image.id.startsWith(pendingPrefix) && <Loader isWithoutBackground variant={LoaderVariants.Local} size={LoaderSizeLG} />}

                    {image.url && (
                      <Button size={ButtonSizeMD} className={classes.downloadBtn} centerIcon={IconDownload} skin={ButtonSkinSecondary} onClick={event => handleImageDownload(event, image)} />
                    )}

                    {!image.url && TaskItemStatus.FAILED_SERVER === image.status && (
                      <View className={classes.warningIcon}>
                        <IconWarningCircleOutline fill={Texts.tint3} />
                      </View>
                    )}

                    {!image.url && TaskItemStatus.FAILED_NSFW === image.status && (
                      <View className={classes.warningIcon}>
                        <IconWarningTriangleOutline fill={Texts.tint1} />
                        <Text size={Typography.t4} align={TypographyHorizontalAlign.Center} color={Texts.tint1}>
                          {mature_info}
                        </Text>
                      </View>
                    )}
                  </ImageFrame>
                </View>
              ))}
            </Grid>
          </View>
          <View ref={bottomRef} />
        </Body>

        <Footer>
          {limitReached && (
            <>
              <View marginBlockStart={Spacings.s8}>
                <Text size={Typography.t4} weight={FontWeights.Bold} align={TypographyHorizontalAlign.Center} color={Statuses.error.base.default}>
                  {free.title}
                </Text>
              </View>
              <View marginBlockStart={Spacings.s8}>
                <Text size={Typography.t4} weight={FontWeights.Regular} align={TypographyHorizontalAlign.Center} color={Statuses.error.base.default}>
                  {free.description}
                </Text>
              </View>
            </>
          )}

          <View flexDirection="column" paddingBlockEnd={Spacings.s24} marginBlockStart={Spacings.s8}>
            <View gap={8}>
              <Button dataTestId="backButton" disabled={pendingResults} className={classes.backBtn} centerIcon={IconChevronLeftLarge} size={ButtonSizeLG} onClick={onBackClick} />
              <Button dataTestId="showMoreButton" isFullWidth isDisabled={limitReached || pendingResults} size={ButtonSizeLG} variant={ButtonVariants.Outline} skin={ButtonSkinPrimary} onClick={loadMore}>
                {pageConfig.generate_button_text}
              </Button>
            </View>
          </View>
        </Footer>
      </Layout>
    </View>
  );
}

export default Results;
